/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { useEffect, useMemo, useState } from 'react'

import {
  AssigneesProjectResponseType,
  getFullNameReversed,
  ResponseOrgFromProject,
  searchOrganizationByInn,
  useCoordinator,
  UUID,
} from '@builddocs/data-management'
import { useAssigneesProjectQuery } from '@builddocs/filestore-api'
import { useDisclosure } from '@builddocs/system'
import {
  Dialog,
  Typography,
  Button,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  DialogContentText,
  TableBody,
  Table,
  TableRow,
  TableCell,
} from '@mui/material'
import _ from 'lodash'
import Draggable from 'react-draggable'

import TableForSelect from './TableForSelect'

type Props = {
  isSmallButton?: boolean
  isSingleChoice?: boolean
  initialAssignees: UUID[]
  isSingleSelect?: boolean
  onSelect: (
    value: AssigneesProjectResponseType[],
    org: {
      inn: string
      ogrn: string
      name: string
      physicalAddress: { unrestrictedValue: string }
    } | null,
  ) => void
  handleUpdate?: () => void
}

function PaperComponent(props) {
  return (
    <Draggable
      cancel={'[class*="MuiDialogContent-root"]'}
      handle="#draggable-dialog-title"
    >
      <Paper {...props} />
    </Draggable>
  )
}

/**
 *Диалог выбора уполномоченных
 */

function AssigneesDialog(props: Props) {
  const {
    isSingleChoice = false,
    onSelect,
    handleUpdate,
    isSmallButton,
    initialAssignees,
    isSingleSelect,
    ...rest
  } = props
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { projectId } = useCoordinator() as { projectId: string }
  const [initialStateAssignees] = useState(initialAssignees)
  const [selected, setSelected] = useState<UUID[]>(initialAssignees)

  const hasValue = useMemo(() => selected.length > 0, [selected])

  const { data: assignees = [], isLoading } =
    useAssigneesProjectQuery(projectId)

  const [allAssignees, setAllAssignees] = useState<
    Record<
      UUID,
      AssigneesProjectResponseType & {
        organizationName: string
        physicalAddress: { unrestrictedValue: string }
        ogrn: string
      }
    >
  >(
    assignees.reduce(
      (acc, assignee) => ({
        ...acc,
        [assignee.employmentId]: {
          ...assignee,
          organizationName: 'Не найдено',
          physicalAddress: { unrestrictedValue: '' },
          ogrn: '',
        },
      }),
      {},
    ),
  )
  const getOrganizationMultiple: (array) => Promise<ResponseOrgFromProject[]> =
    array =>
      Promise.all(
        array.map(inn =>
          searchOrganizationByInn(inn.includes('-') ? inn.split('-')[0] : inn),
        ),
      )

  useEffect(() => {
    const getOrg = () => {
      getOrganizationMultiple(assignees.map(assignee => assignee.inn)).then(
        res =>
          setAllAssignees(
            assignees.reduce(
              (acc, assignee) => ({
                ...acc,
                [assignee.employmentId]: {
                  ...assignee,
                  organizationName: res.find(
                    org => org.data.inn === assignee.inn,
                  )?.value,
                  physicalAddress: {
                    unrestrictedValue: res.find(
                      org => org.data.inn === assignee.inn,
                    )?.data.address.unrestricted_value,
                  },
                  ogrn: res.find(org => org.data.inn === assignee.inn)?.data
                    .ogrn,
                },
              }),
              {},
            ),
          ),
      )
    }

    !isLoading && getOrg()
  }, [assignees, isLoading])

  const handleSelect = (employmentId: UUID | UUID[]) => {
    if (Array.isArray(employmentId)) {
      setSelected(employmentId)
    } else {
      setSelected(prev =>
        prev.includes(employmentId)
          ? prev.filter(Id => Id !== employmentId)
          : isSingleSelect
          ? [employmentId]
          : [...prev, employmentId],
      )
    }
  }
  const handleClose = () => {
    setSelected(initialAssignees)
    onClose()
  }

  const handleSave = () => {
    hasValue
      ? onSelect(
          selected.map(employmentId => allAssignees[employmentId]),
          {
            inn: allAssignees[selected[0]].inn,
            ogrn: allAssignees[selected[0]].ogrn,
            name: allAssignees[selected[0]].organizationName,
            physicalAddress: allAssignees[selected[0]].physicalAddress,
          },
        )
      : onSelect([], null)
    onClose()
  }

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    onOpen()
  }

  useEffect(() => {
    if (assignees.length > 0) {
      setAllAssignees(
        assignees.reduce(
          (acc, assignee) => ({
            ...acc,
            [assignee.employmentId]: {
              ...assignee,
              organizationName: 'Не найдено',
              physicalAddress: { unrestrictedValue: '' },
              ogrn: '',
            },
          }),
          {},
        ),
      )
    }
  }, [assignees])

  return (
    <>
      <Stack alignItems="flex-start" {...rest} mb={2}>
        <Table
          aria-labelledby="tableTitle"
          size="medium"
          sx={{ minWidth: 550 }}
        >
          <TableBody>
            {allAssignees &&
              initialAssignees?.map(employmentId => (
                <TableRow key={employmentId}>
                  <TableCell padding="checkbox">
                    <Typography key={employmentId} variant="subtitle2">
                      {getFullNameReversed(allAssignees[employmentId])}
                    </Typography>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Typography key={employmentId} variant="subtitle2">
                      {allAssignees[employmentId]?.positionDescription}
                    </Typography>
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Typography key={employmentId} variant="subtitle2">
                      {allAssignees[employmentId]?.organizationName}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Stack>
      <Stack direction="row">
        <Button onClick={handleOpen}>
          {isSingleSelect && initialAssignees.length > 0
            ? 'Изменить представителя'
            : 'Выберите представителя'}
        </Button>
        {((isSingleSelect &&
          initialStateAssignees[0] !== initialAssignees[0]) ||
          initialStateAssignees.length !== initialAssignees.length ||
          _.differenceBy(initialStateAssignees, initialAssignees, pers => pers)
            .length > 0) && (
          <Button
            color="secondary"
            size="small"
            onClick={() => {
              setSelected(initialStateAssignees)
              onSelect(
                initialStateAssignees.map(
                  employmentId => allAssignees[employmentId],
                ),
                {
                  inn: allAssignees[selected[0]].inn,
                  ogrn: allAssignees[selected[0]].ogrn,
                  name: allAssignees[selected[0]].organizationName,
                  physicalAddress: allAssignees[selected[0]].physicalAddress,
                },
              )
            }}
          >
            Отменить
          </Button>
        )}
      </Stack>
      <Dialog
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        id="assignees-dialog-modal"
        maxWidth="md"
        open={isOpen}
        onClose={handleClose}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
          Выберите представителя
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Список уполномоченных, участвующих в проекте
          </DialogContentText>
          {allAssignees && (
            <TableForSelect
              isSingleSelect={isSingleSelect}
              items={allAssignees}
              selected={selected}
              tableType="assignees"
              onSelect={handleSelect}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AssigneesDialog
